import type { AnalyticsIdentityService } from '.';
import { effect } from '@preact/signals-react';
import type { AnalyticsService, AnalyticsUserProperties } from '@thinkalpha/ui-ioc/core/types/AnalyticsService';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import type { CurrentUserModel } from 'src/models/CurrentUserModel';
import type { SessionService } from 'src/services/SessionService';

/**
 * Undefined until the security package loads. Reading `isSubscriptionUser` earlier would report
 * every user as a firm user.
 */
const analyticsUserProperties = (currentUser: CurrentUserModel): AnalyticsUserProperties | undefined => {
    const subscriptionStatus = currentUser.subscriptionStatus.value;
    if (subscriptionStatus === undefined) return undefined;

    const isSubscriptionUser = currentUser.isSubscriptionUser.value;
    return { isSubscriptionUser, isFirmUser: !isSubscriptionUser, subscriptionStatus };
};

@injectable()
export class AnalyticsIdentityServiceImpl implements AnalyticsIdentityService {
    readonly #disposableStack = new DisposableStack();
    readonly #logger: Logger;
    readonly #analyticsService: AnalyticsService | undefined;

    constructor(
        @inject('SessionService') sessionService: SessionService,
        @inject('CurrentUserModel') currentUser: CurrentUserModel,
        @inject('Logger') baseLogger: Logger,
        @inject('AnalyticsService') @optional() analyticsService?: AnalyticsService,
    ) {
        this.#logger = baseLogger.getSubLogger({ name: 'analytics-identity' });
        this.#analyticsService = analyticsService;

        this.#disposableStack.defer(
            effect(() => {
                const session = sessionService.state.value;
                switch (session.phase) {
                    case 'authenticating':
                        // Session restore on startup, or a login in progress. Who the visitor is
                        // isn't known yet, and clearing now would give a signed-in user a new
                        // analytics device on every load.
                        return;
                    case 'authenticated':
                    case 'refreshing': {
                        const userDetails = currentUser.user.value;
                        if (userDetails) this.#identify(userDetails, analyticsUserProperties(currentUser));
                        return;
                    }
                    case 'expired':
                    case 'unauthenticated':
                        // Covers an explicit sign-out, and also a stale identity restored from an
                        // earlier visit whose session has since expired.
                        this.#clear(session.phase);
                        return;
                    default:
                        session satisfies never;
                }
            }),
        );
    }

    /**
     * `CurrentUserModel.user` is the effective user, so a superuser masquerading as a customer is
     * tracked as that customer. That is deliberate: masquerade works as "sign in as". Exclude or tag
     * masquerade sessions here if support activity ever needs separating from customer usage.
     */
    #identify(userDetails: UserDetails, properties: AnalyticsUserProperties | undefined): void {
        if (TRACE) {
            this.#logger.trace('Identifying analytics user', { userId: userDetails.id, properties });
        }

        try {
            this.#analyticsService?.identify(userDetails, properties);
        } catch (e) {
            this.#logger.error(`Failed to identify analytics user ${userDetails.id}`, { err: e });
        }
    }

    #clear(phase: 'expired' | 'unauthenticated'): void {
        if (TRACE) {
            this.#logger.trace(`Clearing analytics user for a ${phase} session`);
        }

        try {
            this.#analyticsService?.clearUser();
        } catch (e) {
            this.#logger.error(`Failed to clear analytics user for a ${phase} session`, { err: e });
        }
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
